import * as React from "react";

import twMerge from "@replo/design-system/utils/twMerge";

type InlineSidebarProps = {
  open: boolean;
  className?: string;
  children: React.ReactNode;
};

/**
 * A collapsible inline sidebar component that animates width and opacity.
 * Use this for sidebars that exist within the document flow (not fixed/overlay).
 *
 * For app-level navigation sidebars, use the Sidebar component instead.
 *
 * @example
 * ```tsx
 * <InlineSidebar open={isOpen} className="w-[250px]">
 *   <SidebarContent />
 * </InlineSidebar>
 * ```
 */
export function InlineSidebar({
  open,
  className,
  children,
}: InlineSidebarProps) {
  return (
    <div
      data-state={open ? "expanded" : "collapsed"}
      className={twMerge(
        "flex h-full flex-shrink-0 flex-col overflow-hidden transition-[width,opacity] duration-300 ease-out",
        "data-[state=collapsed]:w-0 data-[state=collapsed]:opacity-0",
        className,
      )}
      aria-hidden={!open}
    >
      {children}
    </div>
  );
}
